import api from "lib/axios";
import { ApiEnvelope, unwrapData } from "./apiTypes";

export type AdminInstructorCourse = {
  id: number;
  title: string;
  description?: string | null;
  image_url?: string | null;
  subject_id?: number | null;
  subject?: { id: number; name: string } | null;
  grade_id?: number | null;
  grade?: { id?: number; name?: string } | null;
  instructor?: { id: number; name?: string | null } | null;
  pricing_type?: "free" | "paid" | string | null;
  price?: number | null;
  currency?: string | null;
  students_count: number;
};

export type AdminInstructorQuizOption = {
  id: number;
  answer: string;
  is_correct: boolean;
};

export type AdminInstructorQuizQuestion = {
  id: number;
  question: string;
  type?: string | null;
  score: number;
  options: AdminInstructorQuizOption[];
};

export type AdminInstructorQuiz = {
  id: number;
  title: string;
  folder_id?: number | null;
  pass_degree?: number;
  max_attempts?: number;
  questions: AdminInstructorQuizQuestion[];
};

export type AdminInstructorContentItem = {
  id: number;
  folder_id: number;
  type: string;
  title: string;
  path?: string | null;
  url?: string | null;
  description?: string | null;
  meta?: Record<string, unknown> | null;
  quiz_id?: number | null;
  quiz?: AdminInstructorQuiz | null;
  is_locked: boolean;
  sort_order: number;
};

export type AdminInstructorContentFolder = {
  id: number;
  parent_id: number | null;
  title: string;
  is_locked: boolean;
  sort_order: number;
  children: AdminInstructorContentFolder[];
  items: AdminInstructorContentItem[];
};

export type AdminInstructorAnnouncement = {
  id: number;
  title: string;
  content?: string | null;
  attachments?: Array<{ name?: string; url?: string; content_type?: string | null }>;
  is_pinned?: boolean;
  is_published?: boolean;
  published_at?: string | null;
};

export type AdminInstructorCourseDetails = {
  course: AdminInstructorCourse;
  content_tree: AdminInstructorContentFolder[];
  quizzes: AdminInstructorQuiz[];
  announcements: AdminInstructorAnnouncement[];
  counts: { students: number; folders: number; items: number; quizzes: number; announcements: number };
};

export type AdminInstructorCoursePayload = {
  title: string;
  description?: string | null;
  pricing_type?: "free" | "paid";
  price?: number | null;
  currency?: string | null;
};

export const adminInstructorCoursesService = {
  async list(instructorId: number | string) {
    const response = await api.get<ApiEnvelope<{ data: AdminInstructorCourse[] }>>(
      `/mcp/instructors/${instructorId}/courses`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<AdminInstructorCourse[]>(response.data) ?? [];
  },
  async update(instructorId: number | string, courseId: number, payload: AdminInstructorCoursePayload) {
    const response = await api.put<ApiEnvelope<{ data: AdminInstructorCourse }>>(
      `/mcp/instructors/${instructorId}/courses/${courseId}`,
      payload
    );
    return unwrapData<AdminInstructorCourse>(response.data);
  },
  async details(instructorId: number | string, courseId: number) {
    const response = await api.get<ApiEnvelope<{ data: AdminInstructorCourseDetails }>>(
      `/mcp/instructors/${instructorId}/courses/${courseId}`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData<AdminInstructorCourseDetails>(response.data);
  },
  async remove(instructorId: number | string, courseId: number) {
    const response = await api.delete(`/mcp/instructors/${instructorId}/courses/${courseId}`);
    return response.data;
  },
};
